import { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { Center, CircularProgress, Flex, Spacer } from '@chakra-ui/react'
import { useAppDispatch } from '../../app/typedReduxHooks'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AuthenticationClient,
  AuthenticationResponse,
} from '../../features/authentication/slice/authenticationSlice'
import { routes } from '../routes'
import { appDidFinishLaunching } from '../../app/appSlice'
import { ssoStorage, applySignInToSSOContext, peekSSOLandingClientName } from '../../features/sso'
import { usePostHog } from 'posthog-js/react'
import { resetIdleDeadline } from '../../utils/idle-timer'
import { AnalyticsProperties, trackAuthLandingExit, trackEvent } from '../../common/utils/analytics'

const CONSUMED_CODE_KEY = 'oauth_code_consumed'

/**
 * Tracks OAuth codes that completed a successful token exchange, per browser
 * tab. sessionStorage rather than localStorage because LoginPage clears
 * localStorage on sign-in, and because storage access can throw outright in
 * private or locked-down browsers - in which case we fall back to the previous
 * behaviour rather than breaking sign-in altogether.
 *
 * Only mark a code after tokens are stored. Marking before the exchange
 * finishes makes a refresh or remount during an in-flight or failed exchange
 * look like a replay and sends the user back to login without tokens.
 */
const wasCodeSuccessfullyConsumed = (code: string): boolean => {
  try {
    return sessionStorage.getItem(CONSUMED_CODE_KEY) === code
  } catch {
    return false
  }
}

const markCodeAsConsumed = (code: string): void => {
  try {
    sessionStorage.setItem(CONSUMED_CODE_KEY, code)
  } catch {
    // Storage unavailable - no-op.
  }
}

// Cognito fails the first sign-in after the pre sign-up trigger links an SSO
// identity to an existing account with "Already found an entry for username
// ...", and the next attempt succeeds. Users saw that as a broken sign-in.
const LINKED_ACCOUNT_ERROR = /already found an entry for username/i

/**
 * The SSO landing to send a failed first sign-in back through, once per tab,
 * with the parameters it was launched with (patient, returnTo and so on).
 * Null when the error is a different one, the callback's state does not prove
 * which landing launched it, this tab has already retried, or the retry can't
 * be recorded (so it could not be limited to one).
 */
const linkedAccountRetryPath = (
  errorDescription: string | null,
  state: string | null
): string | null => {
  if (!errorDescription || !LINKED_ACCOUNT_ERROR.test(errorDescription)) return null
  // Left in place after a failed retry: Back to this callback must not retry
  // again. It expires, and a successful sign-in clears it.
  if (ssoStorage.hasRecentLinkedAccountRetry()) return null
  const clientName = peekSSOLandingClientName(state)
  if (!clientName) return null
  ssoStorage.markLinkedAccountRetry()
  if (!ssoStorage.hasRecentLinkedAccountRetry()) return null
  // The landing that issued the nonce also wrote this context.
  const context = ssoStorage.getContext()
  const params = new URLSearchParams(
    context?.clientName?.toUpperCase() === clientName.toUpperCase() ? context.metadata ?? {} : {}
  ).toString()
  return `/sso/${encodeURIComponent(clientName.toLowerCase())}${params ? `?${params}` : ''}`
}

const AuthenticationPage: FunctionComponent<unknown> = () => {
  const effectRan = useRef(false)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const posthog = usePostHog()

  const posthogRef = useRef(posthog)
  posthogRef.current = posthog
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const navigate = useNavigate()

  const authenticateUser = useCallback(async () => {
    // Every way this page can end somewhere other than the app is recorded
    // under one event, so "how often does sign-in bounce, and why" becomes a
    // single query instead of a guess. Landing back on /login is the expensive
    // outcome: LoginPage's Sign In handler calls localStorage.clear(), so any
    // bounce there throws away a working session and forces a full re-auth -
    // which is what "I had to log in twice" looks like from the inside.
    const exit = (reason: AnalyticsProperties<'auth_landing_exit'>['reason'], to: string) => {
      trackAuthLandingExit(reason, to, Boolean(localStorage.getItem('access_token')))
      return navigate(to)
    }

    try {
      const code = new URLSearchParams(location.search).get('code')
      const error = new URLSearchParams(location.search).get('error')
      const errorDescription = new URLSearchParams(location.search).get('error_description')
      const state = new URLSearchParams(location.search).get('state')
      const isPopupAuth = state === 'popup_auth=true'
      const clientId =
        location?.pathname?.split('/auth/')?.[1] || process.env.REACT_APP_COGNITO_CLIENT_ID
      const isPopup = window.opener && window.opener !== window && isPopupAuth

      if (error || errorDescription) {
        const retryPath = isPopup ? null : linkedAccountRetryPath(errorDescription, state)
        if (retryPath) {
          trackAuthLandingExit(
            'linked_account_retry',
            '/sso',
            Boolean(localStorage.getItem('access_token'))
          )
          return navigate(retryPath, { replace: true })
        }
        if (error) {
          localStorage.setItem('sso_error', error)
        }
        if (errorDescription) {
          localStorage.setItem('sso_error_description', errorDescription)
        }

        if (isPopup) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error, errorDescription }, '*')
          window.close()
        } else {
          return exit('cognito_error', routes.login.path)
        }
        return
      }

      if (!code || !clientId) {
        if (isPopup) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'No code or clientId' }, '*')
          window.close()
        } else {
          return exit('missing_code', routes.login.path)
        }
        return
      }

      // OAuth authorization codes are single use. A remount, a page refresh or
      // the back button re-runs this effect with the same code; the second
      // exchange always fails with invalid_grant and used to send the user back
      // to a blank login page - which reads as "that didn't work", so they sign
      // in again with the very same details and the second attempt succeeds.
      // `effectRan` only guards the StrictMode double-invoke inside a single
      // mount, none of the above.
      if (!isPopup && wasCodeSuccessfullyConsumed(code)) {
        return exit(
          'code_replayed',
          localStorage.getItem('access_token') ? routes.root.path : routes.login.path
        )
      }

      if (isPopup) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code,
              redirectUri: `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`,
              clientId: clientId || '',
            }),
          })

          if (!response.ok) {
            throw new Error('Authentication failed')
          }

          const data = await response.json()
          const { userId } = data || {}

          const expectedUserSub = localStorage.getItem('expected_user_sub')
          if (expectedUserSub && userId !== expectedUserSub) {
            window.opener.postMessage(
              {
                type: 'SSO_AUTH_ERROR',
                error: 'User mismatch - please login with the same account',
              },
              '*'
            )
            window.close()
            return
          }
          window.opener.postMessage({ type: 'SSO_AUTH_SUCCESS' }, '*')
          window.close()
          return
        } catch (error) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'Authentication error' }, '*')
          window.close()
          return
        }
      }
      resetIdleDeadline()
      const { payload } = await dispatch(
        AuthenticationClient.Authenticate({
          code,
          redirectUri: `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`,
          clientId: clientId || '',
        })
      )
      const { assessToken, refreshToken, idToken, userId } =
        (payload as { data?: AuthenticationResponse }).data || {}
      // The internal user id only: PostHog stores person properties offshore,
      // so the clinician's name and email stay out of it (RXP-2552).
      if (userId && posthogRef.current) {
        posthogRef.current.identify(userId)
      }
      // userId is part of the completeness check on purpose. A blank one means
      // the id_token carried no `sub`, and storing tokens in that state leaves
      // the app holding credentials it does not consider authenticated:
      // AuthComponent reads an empty authentication.userId and redirects to
      // /login, where Sign In clears localStorage. Treating it as an incomplete
      // exchange keeps that "tokens present, sent to login anyway" state from
      // existing at all.
      if (assessToken && refreshToken && clientId && idToken && userId) {
        localStorage.setItem('access_token', assessToken)
        localStorage.setItem('refresh_token', refreshToken)
        localStorage.setItem('client_id', clientId)
        localStorage.setItem('id_token', idToken)
        // After the writes, per the note on markCodeAsConsumed: if a setItem
        // throws we would otherwise have burned the code with no tokens to
        // show for it, and the next mount reads that as a replay.
        markCodeAsConsumed(code)
        ssoStorage.clearLinkedAccountRetry()

        // Only once the sign-in has completed, before the app launches. Earlier,
        // a reload while the exchange was pending would find the landing nonce
        // already consumed by the first visit and drop a legitimate launch's
        // patient; a popup re-authentication and a replayed code never get
        // here, so both keep the current patient. Prefers the
        // OAuth-state-validated config, and drops the previous launch's patient
        // when the sign-in didn't start at the SSO landing.
        applySignInToSSOContext(state)
        trackEvent('signed_in', { client: ssoStorage.getContext()?.clientName })

        // The session is now valid and stored. Anything that goes wrong from
        // here is a slow or failed *profile* load, not a failed sign-in, so
        // recover into the app rather than to /login - AuthComponent still
        // redirects to /login on its own if the session is genuinely unusable,
        // and to the error page if the profile load failed outright. Bouncing
        // to /login here discarded a perfectly good session instead.
        timeoutRef.current = setTimeout(() => {
          exit('launch_timeout_30s', routes.root.path)
        }, 30000)

        setTimeout(async () => {
          try {
            await dispatch(appDidFinishLaunching()).unwrap()
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }
            const redirectUrl = ssoStorage.getAndClearRedirectUrl()
            if (redirectUrl) {
              navigate(redirectUrl)
            } else {
              navigate(routes.root.path)
            }
          } catch (error) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }
            exit('launch_failed', routes.root.path)
          }
        }, 1000)
      } else {
        exit('incomplete_tokens', routes.login.path)
      }
    } catch (error) {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'Authentication error' }, '*')
        window.close()
      } else {
        exit('exchange_failed', routes.login.path)
      }
    }
  }, [dispatch, navigate, location.search])

  useEffect(() => {
    if (!effectRan.current) {
      // Marked before the call, not in the cleanup: setting it there meant any
      // dependency change counted as "already ran" while also clearing the
      // fallback timer.
      effectRan.current = true
      authenticateUser()
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [authenticateUser])

  return (
    <Flex flexDirection="column" minWidth="max-content" alignItems="center" gap="10">
      <Spacer />
      <Center w="100px">
        <CircularProgress isIndeterminate color="blue.300" />
      </Center>
      <Center w="intrinsic">We are logging you in...</Center>
    </Flex>
  )
}

export default AuthenticationPage
